<script setup lang="ts">
/**
 * Новый флаг: ключ и среда, в которой его сразу включить.
 *
 * Ключ обязан начинаться с номера задачи — на этом держится уборка отработавших
 * флагов, и сервис откажет ключу другого вида. Проверяем здесь же, чтобы отказ
 * не приходил после нажатия.
 *
 * Заводить отдельную «пустую» запись не нужно: сервис создаёт флаг в обоих
 * файлах сразу и в соседней среде оставляет его выключенным.
 */
import { AppButton, AppModal, AppSelect, AppTextField } from '@vinprop/design-system'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { FLAG_ENVIRONMENTS, setFlag, type FlagEnvironment } from '../api'

const emit = defineEmits<{ done: []; close: [] }>()

const { t } = useI18n()

const key = ref('')
const environment = ref<FlagEnvironment>('dev')
const busy = ref(false)
const error = ref('')

const options = FLAG_ENVIRONMENTS.map((value) => ({ value, label: value }))

/** Тот же вид ключа, что требует сервис: `dev-1415-runtime-flags`. */
const isValid = computed(() => /^dev-\d+-[a-z0-9-]+$/.test(key.value.trim()))

const submit = async () => {
  if (!isValid.value) return

  busy.value = true
  error.value = ''

  try {
    await setFlag(key.value.trim(), environment.value, true)
    emit('done')
  } catch {
    error.value = t('devops.flags.toggleError')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <AppModal
    model-value
    :title="t('devops.flags.newTitle')"
    size="small"
    @update:model-value="emit('close')"
  >
    <div class="new-flag">
      <p class="new-flag__hint">{{ t('devops.flags.newHint') }}</p>

      <AppTextField
        v-model="key"
        :label="t('devops.flags.columns.flag')"
        placeholder="dev-1476-devops-screens"
        :error="key && !isValid ? t('devops.flags.keyInvalid') : error"
      />
      <AppSelect
        v-model="environment"
        :options="options"
        :label="t('devops.flags.filters.environment')"
      />
    </div>

    <template #footer>
      <AppButton variant="secondary" @click="emit('close')">
        {{ t('common.actions.cancel') }}
      </AppButton>
      <AppButton :loading="busy" :disabled="!isValid" @click="submit">
        {{ t('devops.flags.create') }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.new-flag {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.new-flag__hint {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
}
</style>
